(function(global) {
  'use strict';

  if (global.ATUTurfOrdering) return;

  var DEFAULTS = {
    ROLL_WIDTH_FT: 15,
    MAX_ROLL_LENGTH_FT: 100,
    BUFFER_RATE: 0.05,
    MIN_CHECKOUT_SQFT: 375,
    NAILS_SQFT_PER_BOX: 1000,
    WEED_BARRIER_SQFT_PER_ROLL: 1500,
    GOPHER_WIRE_SQFT_PER_ROLL: 400,
    SEAM_TAPE_FT_PER_ROLL: 50
  };

  function numberValue(value) {
    var number = parseFloat(value);
    return Number.isFinite(number) ? number : 0;
  }

  function integerValue(value) {
    var number = parseInt(value, 10);
    return Number.isFinite(number) ? number : 0;
  }

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function optionNumber(options, key, fallback) {
    var number = numberValue(options && options[key]);
    return number > 0 ? number : fallback;
  }

  function normalizedRollText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\u00d7/g, 'x')
      .replace(/[’"]/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  function exactLength(value) {
    var number = numberValue(value);
    if (!number || number < 1) return null;
    return Math.ceil(number);
  }

  function parseRollDimensions(value, options) {
    var rollWidth = optionNumber(options, 'rollWidthFt', DEFAULTS.ROLL_WIDTH_FT);
    var maxLength = optionNumber(options, 'maxRollLengthFt', DEFAULTS.MAX_ROLL_LENGTH_FT);
    var text = normalizedRollText(value);
    var pairMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:ft|feet|foot|')?\s*(?:x|by)\s*(\d+(?:\.\d+)?)\s*(?:ft|feet|foot|')?/i);

    if (!pairMatch) return null;

    var first = numberValue(pairMatch[1]);
    var second = numberValue(pairMatch[2]);
    var length = null;

    if (Math.abs(first - rollWidth) < 0.2 && second > 0) {
      length = exactLength(second);
    } else if (Math.abs(second - rollWidth) < 0.2 && first > 0) {
      length = exactLength(first);
    }

    if (!length || length < 1 || length > maxLength) return null;

    return {
      width: rollWidth,
      length: length,
      area: rollWidth * length,
      label: rollWidth + ' x ' + length
    };
  }

  function parseRollLength(value, options) {
    var parsed = parseRollDimensions(value, options);
    return parsed ? parsed.length : null;
  }

  function splitLinearFeetIntoCuts(totalLinearFt, options) {
    var maxLength = optionNumber(options, 'maxRollLengthFt', DEFAULTS.MAX_ROLL_LENGTH_FT);
    var remaining = Math.max(0, Math.ceil(numberValue(totalLinearFt)));
    var cuts = [];

    while (remaining > 0) {
      var length = Math.min(maxLength, remaining);
      cuts.push(length);
      remaining -= length;
    }

    return cuts;
  }

  function roundUp(value, increment) {
    var step = optionNumber({ roundTo: increment }, 'roundTo', 1);
    return Math.ceil(numberValue(value) / step) * step;
  }

  function rollsForCutSections(sectionLengths, options) {
    var maxLength = optionNumber(options, 'maxRollLengthFt', DEFAULTS.MAX_ROLL_LENGTH_FT);
    var roundTo = optionNumber(options, 'roundTo', 1);
    var rolls = [];
    var currentRollLength = 0;

    toArray(sectionLengths).forEach(function(length) {
      splitLinearFeetIntoCuts(length, { maxRollLengthFt: maxLength }).forEach(function(sectionLength) {
        var roundedSectionLength = roundUp(sectionLength, roundTo);

        if (currentRollLength > 0 && roundUp(currentRollLength + roundedSectionLength, roundTo) > maxLength) {
          rolls.push(roundUp(currentRollLength, roundTo));
          currentRollLength = 0;
        }

        if (roundedSectionLength >= maxLength) {
          rolls.push(maxLength);
          return;
        }

        currentRollLength += roundedSectionLength;
      });
    });

    if (currentRollLength > 0) {
      rolls.push(roundUp(currentRollLength, roundTo));
    }

    return rolls;
  }

  function calculateTurfCuts(projectSqft, options) {
    var rollWidth = optionNumber(options, 'rollWidthFt', DEFAULTS.ROLL_WIDTH_FT);
    var bufferRate = options && options.bufferRate !== undefined ? Math.max(0, numberValue(options.bufferRate)) : DEFAULTS.BUFFER_RATE;
    var measuredSqft = Math.max(0, numberValue(projectSqft));
    var bufferSqft = measuredSqft * bufferRate;
    var requiredSqft = measuredSqft + bufferSqft;
    var requiredLinearFt = requiredSqft > 0 ? Math.ceil(requiredSqft / rollWidth) : 0;
    var lengths = splitLinearFeetIntoCuts(requiredLinearFt, options);
    var orderedSqft = lengths.reduce(function(total, length) {
      return total + rollWidth * length;
    }, 0);

    return {
      measuredSqft: measuredSqft,
      bufferSqft: bufferSqft,
      requiredSqft: requiredSqft,
      requiredLinearFt: requiredLinearFt,
      cuts: lengths.map(function(length) {
        return {
          width: rollWidth,
          length: length,
          area: rollWidth * length
        };
      }),
      orderedSqft: orderedSqft,
      extraSqft: Math.max(0, orderedSqft - measuredSqft)
    };
  }

  function variantText(variant) {
    if (!variant) return '';

    return [
      variant.title,
      variant.option1,
      variant.option2,
      variant.option3,
      variant.sku
    ].filter(Boolean).join(' ');
  }

  function variantRollLength(variant, options) {
    var explicitLength = exactLength(variant && variant.rollLength);
    if (explicitLength) return explicitLength;

    var parsed = parseRollLength(variantText(variant), options);
    if (parsed) return parsed;

    if (variant && Array.isArray(variant.options)) {
      for (var index = 0; index < variant.options.length; index += 1) {
        parsed = parseRollLength(variant.options[index], options);
        if (parsed) return parsed;
      }
    }

    return null;
  }

  function findExactVariantForCut(product, width, length, options) {
    var rollWidth = width || optionNumber(options, 'rollWidthFt', DEFAULTS.ROLL_WIDTH_FT);
    var targetLength = exactLength(length);
    var availableMatch = null;
    var unavailableMatch = null;

    if (!product || !Array.isArray(product.variants) || !targetLength) return null;

    product.variants.forEach(function(variant) {
      var parsedLength = variantRollLength(variant, Object.assign({}, options || {}, {
        rollWidthFt: rollWidth
      }));

      if (parsedLength !== targetLength) return;

      if (variant.available && !availableMatch) {
        availableMatch = variant;
      } else if (!unavailableMatch) {
        unavailableMatch = variant;
      }
    });

    return availableMatch || unavailableMatch;
  }

  function recommendedSupplyQuantity(accessory, state) {
    var projectSqft = Math.max(0, numberValue(state && (state.projectSqft || state.measuredSqft || state.sqft)));
    var seamLinearFt = Math.max(0, numberValue(state && state.seamLinearFt));
    var coverage = numberValue(accessory && accessory.coverageSqft);
    var key = accessory && accessory.key;

    if (!accessory || projectSqft <= 0) return 0;
    if (key === 'seam_tape') return Math.ceil(seamLinearFt / (coverage || DEFAULTS.SEAM_TAPE_FT_PER_ROLL));
    if (key === 'nails') return Math.max(1, Math.ceil(projectSqft / (coverage || DEFAULTS.NAILS_SQFT_PER_BOX)));
    if (key === 'weed_barrier') return Math.max(1, Math.ceil(projectSqft / (coverage || DEFAULTS.WEED_BARRIER_SQFT_PER_ROLL)));
    if (key === 'gopher_wire') return Math.max(1, Math.ceil(projectSqft / (coverage || DEFAULTS.GOPHER_WIRE_SQFT_PER_ROLL)));

    return coverage > 0 ? Math.max(1, Math.ceil(projectSqft / coverage)) : 1;
  }

  function calculateSupplyQuantities(projectSqftOrState, accessories) {
    var state = typeof projectSqftOrState === 'object'
      ? projectSqftOrState
      : { projectSqft: numberValue(projectSqftOrState) };

    if (Array.isArray(accessories)) {
      return accessories.reduce(function(result, accessory) {
        if (accessory && accessory.key) {
          result[accessory.key] = recommendedSupplyQuantity(accessory, state);
        }
        return result;
      }, {});
    }

    return {
      nails: recommendedSupplyQuantity({ key: 'nails' }, state),
      weed_barrier: recommendedSupplyQuantity({ key: 'weed_barrier' }, state),
      gopher_wire: recommendedSupplyQuantity({ key: 'gopher_wire' }, state),
      seam_tape: recommendedSupplyQuantity({ key: 'seam_tape' }, state)
    };
  }

  function itemOptionText(options) {
    if (!Array.isArray(options)) return '';

    return options.map(function(option) {
      return option ? [option.name, option.value].filter(Boolean).join(' ') : '';
    }).join(' ');
  }

  function itemPropertyValue(item, names) {
    var properties = item && item.properties;
    var wanted = toArray(names).map(function(name) {
      return String(name).toLowerCase();
    });

    if (!properties) return '';

    if (Array.isArray(properties)) {
      for (var index = 0; index < properties.length; index += 1) {
        var property = properties[index];
        var key = String((property && (property.name || property.key || property[0])) || '').toLowerCase();

        if (wanted.indexOf(key) !== -1) {
          return (property && (property.value || property[1])) || '';
        }
      }

      return '';
    }

    for (var propertyName in properties) {
      if (Object.prototype.hasOwnProperty.call(properties, propertyName) && wanted.indexOf(String(propertyName).toLowerCase()) !== -1) {
        return properties[propertyName];
      }
    }

    return '';
  }

  function itemPropertyText(item) {
    var properties = item && item.properties;

    if (!properties) return '';

    if (Array.isArray(properties)) {
      return properties.map(function(property) {
        return [property && (property.name || property.key || property[0]), property && (property.value || property[1])].filter(Boolean).join(' ');
      }).join(' ');
    }

    return Object.keys(properties).map(function(key) {
      return [key, properties[key]].filter(Boolean).join(' ');
    }).join(' ');
  }

  function itemSearchText(item) {
    return [
      item && item.variant_title,
      item && item.title,
      item && item.product_title,
      item && item.handle,
      item && item.url,
      itemOptionText(item && item.options_with_values),
      itemPropertyText(item)
    ].filter(Boolean).join(' ');
  }

  function isExcludedCartItem(item) {
    var text = itemSearchText(item).toLowerCase();

    return /sample|remnant|living wall|boxwood|ivy|accessor|nail|staple|seam tape|glue|weed barrier|infill|deodorizer|gopher wire|edging|bender board|golf cup|flag pole/.test(text);
  }

  function parseSqftValue(value) {
    var match = String(value || '').match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : 0;
  }

  function rollFromCartItem(item, options) {
    if (isExcludedCartItem(item)) return null;

    var rollWidth = optionNumber(options, 'rollWidthFt', DEFAULTS.ROLL_WIDTH_FT);
    var coverage = parseSqftValue(itemPropertyValue(item, ['Cut Coverage', 'cut_coverage', 'Coverage Sq Ft']));

    if (coverage > 0) {
      return {
        width: rollWidth,
        length: Math.ceil(coverage / rollWidth),
        area: coverage,
        label: itemPropertyValue(item, ['Cut Size', 'Recommended Roll']) || rollWidth + ' x ' + Math.ceil(coverage / rollWidth)
      };
    }

    return parseRollDimensions(itemSearchText(item), options);
  }

  function normalizeHandle(value) {
    var text = String(value || '').toLowerCase();
    var urlMatch = text.match(/\/products\/([^/?#]+)/);
    return urlMatch ? urlMatch[1] : text;
  }

  function productUrlFromHandle(handle) {
    var normalizedHandle = normalizeHandle(handle);
    return normalizedHandle ? '/products/' + normalizedHandle : '';
  }

  function productNameFromItem(item) {
    return String((item && item.product_title) || (item && item.title) || 'Turf roll').replace(/\s+-\s+15\s*x\s*\d{1,3}.*$/i, '');
  }

  function collectCartTurfSummary(items, options) {
    var productMap = {};
    var summary = {
      area: 0,
      quantity: 0,
      sizes: [],
      products: []
    };

    toArray(items).forEach(function(item) {
      var parsed = rollFromCartItem(item, options);

      if (!parsed) return;

      var handle = normalizeHandle((item && item.handle) || (item && item.url) || (item && item.product_handle));
      var quantity = Math.max(1, Number(item && item.quantity) || 1);
      var productName = productNameFromItem(item);
      var productUrl = productUrlFromHandle(handle);

      summary.area += parsed.area * quantity;
      summary.quantity += quantity;
      summary.sizes.push(productName + ' ' + parsed.label + (quantity > 1 ? ' x ' + quantity : ''));

      if (productUrl && !productMap[productUrl]) {
        productMap[productUrl] = true;
        summary.products.push({
          title: productName,
          url: productUrl
        });
      }
    });

    return summary;
  }

  function calculateCartTurfSqft(cartItems, options) {
    return collectCartTurfSummary(cartItems, options).area;
  }

  function isCheckoutMinimumMet(cartItems, options) {
    var minimum = optionNumber(options, 'minCheckoutSqft', DEFAULTS.MIN_CHECKOUT_SQFT);
    var total = calculateCartTurfSqft(cartItems, options);

    return total <= 0 || total >= minimum;
  }

  var api = {
    DEFAULTS: DEFAULTS,
    numberValue: numberValue,
    integerValue: integerValue,
    parseRollDimensions: parseRollDimensions,
    parseRollLength: parseRollLength,
    splitLinearFeetIntoCuts: splitLinearFeetIntoCuts,
    rollsForCutSections: rollsForCutSections,
    calculateTurfCuts: calculateTurfCuts,
    variantRollLength: variantRollLength,
    findExactVariantForCut: findExactVariantForCut,
    recommendedSupplyQuantity: recommendedSupplyQuantity,
    calculateSupplyQuantities: calculateSupplyQuantities,
    rollFromCartItem: rollFromCartItem,
    collectCartTurfSummary: collectCartTurfSummary,
    calculateCartTurfSqft: calculateCartTurfSqft,
    isCheckoutMinimumMet: isCheckoutMinimumMet
  };

  global.ATUTurfOrdering = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
